import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getHourlyFakeBase } from '../../../lib/hourlyCount';

interface RegionStat {
  city: string;
  count: number;
  avgBudget: number;
  icon: string;
  color: string;
  barColor: string;
}

// 基礎分布資料（固定比例，依實際總數縮放）
const BASE_REGIONS: Omit<RegionStat, 'count'>[] = [
  { city: '台北市', avgBudget: 28000, icon: 'ri-building-2-line', color: 'text-violet-600', barColor: 'bg-violet-400' },
  { city: '新北市', avgBudget: 20000, icon: 'ri-map-2-line',       color: 'text-sky-600',    barColor: 'bg-sky-400' },
  { city: '台中市', avgBudget: 19000, icon: 'ri-city-line',         color: 'text-teal-600',   barColor: 'bg-teal-400' },
  { city: '高雄市', avgBudget: 17000, icon: 'ri-anchor-line',       color: 'text-emerald-600',barColor: 'bg-emerald-400' },
  { city: '桃園市', avgBudget: 15000, icon: 'ri-train-line',        color: 'text-orange-600', barColor: 'bg-orange-400' },
  { city: '台南市', avgBudget: 15000, icon: 'ri-ancient-gate-line', color: 'text-amber-600',  barColor: 'bg-amber-400' },
  { city: '新竹縣市', avgBudget: 22000, icon: 'ri-cpu-line',         color: 'text-indigo-600', barColor: 'bg-indigo-400' },
  { city: '其他地區', avgBudget: 14000, icon: 'ri-map-pin-2-line',   color: 'text-slate-500',  barColor: 'bg-slate-300' },
];

// 各城市比例（加總 = 1）
const RATIOS = [0.195, 0.176, 0.158, 0.135, 0.119, 0.104, 0.062, 0.051];

interface Props {
  theme?: 'tech' | 'pink' | 'white';
}

export default function RegionDistributionSection({ theme = 'tech' }: Props) {
  const [totalCount, setTotalCount] = useState<number>(getHourlyFakeBase());
  const [realCount, setRealCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { count } = await supabase
          .from('tenant_match_requests')
          .select('id', { count: 'exact', head: true });
        if (count !== null) setRealCount(count);
      } catch { /* silent */ }
    };
    fetchCount();
  }, []);

  const displayTotal = getHourlyFakeBase() + realCount;

  useEffect(() => {
    setTotalCount(displayTotal);
  }, [displayTotal]);

  const regions: RegionStat[] = BASE_REGIONS.map((r, i) => ({
    ...r,
    count: Math.round(totalCount * RATIOS[i]),
  }));

  const maxCount = Math.max(...regions.map(r => r.count));

  const sectionBg =
    theme === 'pink'  ? 'bg-rose-50  border-rose-100'  :
    theme === 'white' ? 'bg-gray-50  border-gray-200'   :
                        'bg-stone-50 border-stone-100';

  const accentBtn =
    theme === 'pink'  ? 'bg-rose-400  hover:bg-rose-500  text-white' :
    theme === 'white' ? 'bg-gray-900  hover:bg-gray-800  text-white' :
                        'bg-teal-500  hover:bg-teal-600  text-white';

  return (
    <section className={`rounded-2xl border p-5 ${sectionBg}`}>
      {/* 標題列 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-xl">
            <i className="ri-bar-chart-2-fill text-white text-base"></i>
          </div>
          <div>
            <h3 className="text-sm font-black text-gray-900">熱門租屋需求地區</h3>
            <p className="text-[10px] text-gray-500">目前等待中的租客地區分布</p>
          </div>
        </div>
        {/* 即時計數 */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full">
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
          </span>
          <span className="text-xs font-black text-gray-800 tabular-nums">{totalCount}</span>
          <span className="text-xs text-gray-500">位等待中</span>
        </div>
      </div>

      {/* 地區橫向 Bar Chart */}
      <div className="space-y-2.5 mb-5">
        {regions.map((region) => {
          const pct = maxCount > 0 ? (region.count / maxCount) * 100 : 0;
          return (
            <div key={region.city} className="flex items-center gap-3">
              {/* 城市名 + 圖示 */}
              <div className="flex items-center gap-1.5 w-20 flex-shrink-0">
                <i className={`${region.icon} text-sm flex-shrink-0 ${region.color}`}></i>
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{region.city}</span>
              </div>
              {/* 橫向進度條 */}
              <div className="flex-1 bg-gray-200 rounded-full h-5 relative overflow-hidden">
                <div
                  className={`h-full rounded-full flex items-center px-2.5 transition-all duration-700 ${region.barColor}`}
                  style={{ width: `${Math.max(pct, 8)}%` }}
                >
                  <span className="text-[10px] font-black text-white whitespace-nowrap">
                    {region.count} 人
                  </span>
                </div>
              </div>
              {/* 平均預算 */}
              <div className="w-20 flex-shrink-0 text-right">
                <span className="text-[10px] text-gray-500">均 </span>
                <span className="text-[10px] font-bold text-gray-700">
                  ${(region.avgBudget / 1000).toFixed(0)}k
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* 分隔線 */}
      <div className="border-t border-gray-200 mb-4"></div>

      {/* 統計卡片行 */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { icon: 'ri-user-3-fill', color: 'text-teal-500', bg: 'bg-teal-50 border-teal-100', label: '等待租客', value: `${totalCount}+` },
          { icon: 'ri-money-dollar-circle-fill', color: 'text-amber-500', bg: 'bg-amber-50 border-amber-100', label: '平均月預算', value: '$19k' },
          { icon: 'ri-map-pin-fill', color: 'text-violet-500', bg: 'bg-violet-50 border-violet-100', label: '涵蓋地區', value: '19縣市' },
        ].map(item => (
          <div key={item.label} className={`rounded-xl border px-3 py-2.5 text-center ${item.bg}`}>
            <i className={`${item.icon} ${item.color} text-base mb-0.5 block`}></i>
            <p className="text-base font-black text-gray-900 leading-none">{item.value}</p>
            <p className="text-[9px] text-gray-500 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* 屋主 CTA */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
            <i className="ri-home-smile-2-fill text-amber-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-black text-gray-900">你的房源有人在等！</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              上方任何一個城市都有數十位租客正在等待，免費刊登後 AI 立即比對，不怕沒人詢問
            </p>
          </div>
        </div>
        <Link
          to="/publish"
          className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-black text-sm transition-colors cursor-pointer whitespace-nowrap ${accentBtn}`}
        >
          <i className="ri-home-4-line text-base"></i>
          免費刊登房源，AI 自動配對
          <i className="ri-arrow-right-line text-base"></i>
        </Link>
      </div>
    </section>
  );
}
